/**
 * Offline Banner Component
 *
 * Displays a persistent banner when the user is offline.
 * Shows a success message briefly when connection is restored.
 */

import React from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

const OfflineBanner: React.FC = () => {
  const { t } = useTranslation();
  const { isOnline, wasOffline } = useNetworkStatus();

  // Show "back online" message briefly
  if (isOnline && wasOffline) {
    return (
      <div
        className="fixed top-0 left-0 right-0 z-50 bg-green-600 text-white px-4 py-3 shadow-lg animate-slide-down"
        role="alert"
        aria-live="polite"
      >
        <div className="container mx-auto flex items-center justify-center">
          <Wifi className="w-5 h-5 mr-3 flex-shrink-0" aria-hidden="true" />
          <p className="font-medium text-sm sm:text-base">
            {t('errors.connection_restored', 'Connexion rétablie. Vous pouvez continuer.')}
          </p>
        </div>
      </div>
    );
  }

  // Show offline banner
  if (!isOnline) {
    return (
      <div
        className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 text-gray-900 px-4 py-3 shadow-lg"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div className="container mx-auto flex items-center justify-center">
          <WifiOff className="w-5 h-5 mr-3 flex-shrink-0" aria-hidden="true" />
          <div className="flex-1">
            <p className="font-medium text-sm sm:text-base">
              {t(
                'errors.offline_message',
                'Vous êtes hors ligne. Vos données seront synchronisées à la reconnexion.'
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OfflineBanner;
